import axios from "axios";

// Empty REACT_APP_BACKEND_URL → same-origin /api (nginx or CRA setupProxy).
const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
export const API = BASE ? `${BASE}/api` : "/api";

const client = axios.create({ baseURL: API, withCredentials: true });

client.interceptors.request.use((config) => {
  const csrf = sessionStorage.getItem("lg_csrf");
  if (csrf) {
    config.headers = config.headers || {};
    config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

function storeCsrf(data) {
  if (data?.csrf_token) sessionStorage.setItem("lg_csrf", data.csrf_token);
  return data;
}

export const api = {
  tenants: () => client.get("/tenants").then((r) => r.data),
  login: async (body) => storeCsrf(await client.post("/auth/login", body).then((r) => r.data)),
  authSession: async () => storeCsrf(await client.get("/auth/session").then((r) => r.data)),
  logout: () => client.post("/auth/logout").then((r) => r.data),
  actionProposalState: () => client.get("/action-proposals/state").then((r) => r.data),
  actionProposalCreate: () => client.post("/action-proposals/create").then((r) => r.data),
  actionProposalAdvice: (body) =>
    client.post("/action-proposals/advice", body).then((r) => r.data),
  actionProposalApprove: (body) =>
    client.post("/action-proposals/approve", body).then((r) => r.data),
  actionProposalReject: (body) =>
    client.post("/action-proposals/reject", body).then((r) => r.data),
  actionProposalPolicy: (body) =>
    client.post("/action-proposals/policy", body).then((r) => r.data),
  sectorPacks: () => client.get("/sector-packs").then((r) => r.data),
  providers: () => client.get("/providers").then((r) => r.data),

  departments: (tid) => client.get(`/tenants/${tid}/departments`).then((r) => r.data),
  addDepartment: (tid, body) => client.post(`/tenants/${tid}/departments`, body).then((r) => r.data),
  delDepartment: (did) => client.delete(`/departments/${did}`).then((r) => r.data),

  users: (tid) => client.get(`/tenants/${tid}/users`).then((r) => r.data),
  addUser: (tid, body) => client.post(`/tenants/${tid}/users`, body).then((r) => r.data),
  delUser: (uid) => client.delete(`/users/${uid}`).then((r) => r.data),
  signoff: (uid, body) => client.post(`/users/${uid}/signoff`, body).then((r) => r.data),

  policy: (tid, deptId) =>
    client.get(`/tenants/${tid}/policy`, { params: { department_id: deptId } }).then((r) => r.data),
  updatePolicy: (tid, body) => client.put(`/tenants/${tid}/policy`, body).then((r) => r.data),
  setPolicyPreset: (tid, body) => client.post(`/tenants/${tid}/policy/preset`, body).then((r) => r.data),
  setPresetConfig: (tid, categories, user_id) =>
    client.put(`/tenants/${tid}/policy/preset-config`, { categories, user_id }).then((r) => r.data),
  policyEvents: (tid) => client.get(`/tenants/${tid}/policy-events`).then((r) => r.data),
  updateAllowlist: (tid, body) => client.put(`/tenants/${tid}/allowlist`, body).then((r) => r.data),

  customCategories: (tid) => client.get(`/tenants/${tid}/custom-categories`).then((r) => r.data),
  addCustomCategory: (tid, body) =>
    client.post(`/tenants/${tid}/custom-categories`, body).then((r) => r.data),
  delCustomCategory: (cid) => client.delete(`/custom-categories/${cid}`).then((r) => r.data),
  testPattern: (body) => client.post(`/custom-categories/test`, body).then((r) => r.data),

  chat: (body) => client.post("/gateway/chat", body).then((r) => r.data),
  /** Streaming chat: credentials + CSRF (raw fetch; axios does not stream SSE). */
  chatStream: (body, { signal } = {}) => {
    const csrf = sessionStorage.getItem("lg_csrf");
    const headers = { "Content-Type": "application/json" };
    if (csrf) headers["X-CSRF-Token"] = csrf;
    return fetch(`${API}/gateway/chat/stream`, {
      method: "POST",
      headers,
      credentials: "include",
      body: JSON.stringify(body),
      signal,
    });
  },
  uploadFile: (formData) =>
    client
      .post("/files/upload", formData, { headers: { "Content-Type": "multipart/form-data" } })
      .then((r) => r.data),
  verifyAudit: (auditId, body = {}) =>
    client.post(`/audit/${auditId}/verify`, body).then((r) => r.data),
  deleteAudit: (auditId, body = {}) =>
    client.delete(`/audit/${auditId}`, { data: body }).then((r) => r.data),
  audit: (tid, params) => client.get(`/tenants/${tid}/audit`, { params }).then((r) => r.data),
  conversationTurns: (tid, cid, params = {}) =>
    client.get(`/tenants/${tid}/conversations/${cid}/turns`, { params }).then((r) => r.data),
  dashboard: (tid) => client.get(`/tenants/${tid}/dashboard`).then((r) => r.data),
  report: (tid, month) =>
    client.get(`/tenants/${tid}/report`, { params: { month } }).then((r) => r.data),
  verifyReport: (tid, month) =>
    client.get(`/tenants/${tid}/report/verify`, { params: month ? { month } : {} }).then((r) => r.data),
  conversations: (tid, params = {}) =>
    client.get(`/tenants/${tid}/conversations`, { params }).then((r) => r.data),
  deleteConversation: (tid, cid, body = {}) =>
    client.delete(`/tenants/${tid}/conversations/${cid}`, { data: body }).then((r) => r.data),

  onboardPreview: (body) => client.post("/onboarding/preview", body).then((r) => r.data),
  onboard: (body) => client.post("/onboarding", body).then((r) => r.data),
  activate: (tid) => client.post(`/tenants/${tid}/activate`).then((r) => r.data),

  integration: (tid) => client.get(`/integration/${tid}`).then((r) => r.data),

  platformLogin: (body) => client.post("/platform/login", body).then((r) => r.data),
  platformOverview: () => client.get("/platform/overview").then((r) => r.data),
  gateVerify: (body) => client.post("/gate/verify", body).then((r) => r.data),
  version: () => client.get("/version").then((r) => r.data),
};
